import { PropsWithChildren, useEffect, useState } from 'react';
import useTriggerRequest from '@/hooks/useTriggerRequest';
import { applyCoupon, fetchCoupons, validateCoupon } from '@/actions/ecom/coupon';
import { CouponItem } from '@/components/Product/AddToCart';
import Drawer from './Drawer';
import { useEcomProductContext } from '@/context/ecomProductContext';
import is from '@/utils/is';
import VALIDATION_ERRORS from '@/constants/validationErrors';
import CouponContent from './CouponContent';
import dlv from 'dlv';
import { EcomGTMEventNames } from '@/types/ecomGTM';
import useEcomGTM from '@/hooks/useEcomGTM';

export interface CouponProps {
  open: boolean;
  onClose: () => void;
  onApply: () => void;
  setCart: (cartData: any) => void;
  couponsData?: CouponItem[];
  cartDetails?: {
    cartId?: string;
    orderAmount?: number;
    cartItems?: any[];
  };
}

function CouponList(props: PropsWithChildren<CouponProps>) {
  const { open, onClose, onApply, couponsData, cartDetails, setCart } = props;
  const [coupons, setCoupons] = useState<any>([]);

  const fetchCouponsData = (body: any) => fetchCoupons.apply(null, [body]);
  const couponList = useTriggerRequest({ fetch: fetchCouponsData });

  const couponValidate = (body: any) => validateCoupon.apply(null, [body]);
  const couponValidateTrigger = useTriggerRequest({ fetch: couponValidate });

  const couponApply = (body: any) => applyCoupon.apply(null, [body]);
  const couponApplyTrigger = useTriggerRequest({ fetch: couponApply });

  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const { state: ecomProductState, dispatch: ecomProductDispatch } = useEcomProductContext();
  const { fireEcomGTMEvent } = useEcomGTM();

  const setCouponData = (couponsResponse: any) => {
    const coupons = couponsResponse?.data?.data as [];
    setCoupons(filterList(coupons));
  };

  const onModalClose = () => {
    setErrorMessage(undefined);
    onClose();
  };

  const filterList = (coupons: any[]) => {
    return coupons.filter((coupon) => coupon.applicableDisc > 0);
  };

  const couponListPaylod = () => {
    const productList =
      cartDetails?.cartItems?.map((item: any) => {
        const { productId = '', quantity = 0, sellingPrice } = item?.item ?? {};
        return {
          productId,
          quantity,
          mop: sellingPrice,
        };
      }) ?? [];
    return {
      orderAmount: cartDetails?.orderAmount,
      products: productList,
    };
  };

  useEffect(() => {
    const fetchCoupons = async () => {
      if (couponsData) {
        setCoupons(filterList(couponsData));
      } else {
        if (open) {
          setCoupons([]);
          const couponsResponse = await couponList.promisifyTrigger({ ...couponListPaylod() });
          setCouponData(couponsResponse);
        }
      }
    };
    fetchCoupons();
  }, [open]);

  async function onApplyCoupon(coupon: any) {
    const handleCouponResponse = async (couponCode: string) => {
      const response = await validateCouponFunc(couponCode);
      if (response.status === 200) {
        return {
          coupon: response.data.data.totalCouponDiscount.couponCode,
          applicableDisc: response.data.data.totalCouponDiscount.applicableDisc,
        };
      } else {
        throw new Error(VALIDATION_ERRORS['invalidCoupon']);
      }
    };

    const applyCouponToCart = async (coupon: any) => {
      const cartResponse = await couponApplyTrigger.promisifyTrigger({
        event: 'apply',
        cartId: cartDetails?.cartId,
        coupon: coupon.coupon || coupon,
      });
      if (cartResponse.status === 200) {
        onModalClose();
        setCart(cartResponse);
      } else {
        throw new Error(VALIDATION_ERRORS['invalidCoupon']);
      }
    };

    try {
      if (is.string(coupon)) {
        if (couponsData) {
          const validatedCoupon = await handleCouponResponse(coupon);
          updateCoupon(validatedCoupon);
        } else {
          await applyCouponToCart(coupon);
        }
      } else {
        if (couponsData) {
          updateCoupon(coupon);
        } else {
          await applyCouponToCart(coupon);
        }
      }
    } catch (error) {
      const errorString = dlv(error as Object, 'error', VALIDATION_ERRORS['invalidCoupon']);
      setErrorMessage(errorString);
    }
  }

  function updateCoupon(coupon: any) {
    ecomProductDispatch.updateAppliedCoupon({
      materialId: ecomProductState.currentProduct.materialId,
      appliedCoupon: coupon,
    });
    fireEcomGTMEvent(EcomGTMEventNames.PDP.COUPON_APPLY_STARTED, {
      selected_coupon_name: coupon?.coupon,
      selected_coupon_value: coupon?.applicableDisc,
      selected_coupon_validity: coupon?.expiry,
    });
    onModalClose();
    onApply();
  }

  async function validateCouponFunc(coupon: string): Promise<any> {
    return await couponValidateTrigger.promisifyTrigger({
      totalOriginalSellingPrice: ecomProductState.currentProduct.price.mop,
      items: [
        {
          itemId: ecomProductState.currentProduct?.productId ?? '',
          quantity: ecomProductState.currentProduct?.quantity?.min ?? 1,
          originalSellingPrice: ecomProductState.currentProduct.price.mop,
          parentItemId: null,
        },
      ],
      userId: null,
      couponCode: coupon,
      paymentMode: 'Online',
    });
  }

  return (
    <Drawer modalOpen={open} onClose={onModalClose} showCloseButton={false} className={'!p-0'}>
      {CouponContent(coupons, onModalClose, onApplyCoupon, cartDetails?.cartId, errorMessage)}
    </Drawer>
  );
}

export default CouponList;
